import React, { useState } from "react";
import {
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Collapse,
  List,
  Divider,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Box
} from "@mui/material";
import { useStateValue } from "../../../contexto/store";
import { Link, useLocation } from "react-router-dom";
import IconExpandLess from "@mui/icons-material/ExpandLess";
import IconExpandMore from "@mui/icons-material/ExpandMore";
import { menuConfig, hasRole } from "../../../config/menuConfig";
import { useTheme } from "@mui/material/styles";
import { alpha } from '@mui/material/styles';
import AvTimerIcon from "@mui/icons-material/AvTimer";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useMeasurerByCode } from "../../../hooks/useMeasurers";

// ── Derive which sections should be open based on the current pathname ────────
// Called once as useState lazy initializer → no re-computation on every render.
const getInitialOpenSections = (pathname) => {
  const open = {};
  menuConfig.forEach((section) => {
    if (section.children?.some((child) => child.link === pathname)) {
      open[section.id] = true;
    }
  });
  return open;
};

const CajonIzquierda = () => {
  const [{ sesionUsuario, drawer }] = useStateValue();
  const location = useLocation();
  const theme = useTheme();
  const isCollapsed = drawer?.isCollapsed || false;

  // Lazy initializer: open the section that matches the current URL on first render
  const [openSections, setOpenSections] = useState(() => {
    const initial = getInitialOpenSections(location.pathname);
    // Associate measurer sub-items are dynamic; open "Mi Cuenta" when inside its routes
    if (location.pathname.startsWith('/roleAssociate')) {
      initial['associate'] = true;
    }
    return initial;
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);


  // For associate users, load their measurers to show them as sub-menu items
  const rolName = sesionUsuario?.usuario?.rolName;
  const isAssociate = rolName === 'Associate';
  const { data: associateMeasurers } = useMeasurerByCode(
    isAssociate ? sesionUsuario?.usuario?.username : null
  );

  if (!sesionUsuario || !sesionUsuario.usuario) return null;

  const handleToggle = (event, section) => {
    if (section.link) {
      handleCloseMenu();
      return; // Link component handles navigation
    }

    if (isCollapsed) {
      setAnchorEl(event.currentTarget);
      setActiveMenuId(section.id);
    } else {
      setOpenSections((prev) => ({
        ...prev,
        [section.id]: !prev[section.id],
      }));
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveMenuId(null);
  };

  const allowedMenuItems = menuConfig
    .filter((section) => hasRole(sesionUsuario.usuario, section.roles))
    .map((section) => {
      // Enrich the associate "Mi Cuenta" section with its measurers as sub-items
      if (section.id === 'associate' && isAssociate && associateMeasurers?.length) {
        return {
          ...section,
          link: undefined, // turn into an expandable section
          children: [
            { title: 'Resumen', icon: <DashboardIcon />, link: '/roleAssociate/dashboard' },
            ...associateMeasurers.map((m) => ({
              title: `Medidor ${m.code}`,
              icon: <AvTimerIcon />,
              link: `/roleAssociate/measurer/${m.measurerId}/debts`,
            })),
          ],
        };
      }
      return section;
    });

  return (
    <>
      <List component="nav" sx={{ width: '100%', p: 0 }}>
        {allowedMenuItems.map((section) => {
          const hasChildren = section.children && section.children.length > 0;
          const isOpen = openSections[section.id] || false;
          const isActive = section.link 
            ? location.pathname === section.link 
            : section.children?.some(child => child.link === location.pathname);
          const isMenuOpen = activeMenuId === section.id;

          return (
            <React.Fragment key={section.id}>
              <Tooltip 
                title={isCollapsed && !isMenuOpen ? section.title : ""} 
                placement="right" 
                arrow
              >
                <ListItemButton
                  component={section.link ? Link : 'div'}
                  to={section.link || undefined}
                  onClick={(e) => handleToggle(e, section)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    px: isCollapsed ? 1 : 2,
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    backgroundColor: (isActive && (section.link || (!isOpen && !isCollapsed))) ? alpha(theme.palette.common.white, 0.15) : 'transparent',
                    color: (isActive && (section.link || (!isOpen && !isCollapsed))) ? 'common.white' : alpha(theme.palette.common.white, 0.7),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.08),
                      color: 'common.white'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: isCollapsed ? 0 : 40, 
                    mr: isCollapsed ? 0 : 0,
                    justifyContent: 'center',
                    color: 'inherit'
                  }}>
                    {section.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={section.title}
                      slotProps={{ primary: { fontWeight: (isActive && (section.link || (!isOpen))) ? 700 : 500, color: 'inherit', noWrap: true } }}
                    />
                  )}
                  {!isCollapsed && hasChildren && (isOpen ? <IconExpandLess sx={{ color: 'inherit' }} /> : <IconExpandMore sx={{ color: 'inherit' }} />)}
                </ListItemButton>
              </Tooltip>

              {/* Mini Sidebar Floating Menu */}
              {isCollapsed && isMenuOpen && hasChildren && (
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{ paper: {
                    sx: {
                      ml: 1,
                      minWidth: 180,
                      bgcolor: 'primary.darker',
                      color: 'common.white',
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                      boxShadow: theme.shadows[10],
                      '& .MuiList-root': { p: 1 }
                    }
                  } }}
                >
                  <Box sx={{ px: 2, py: 1, mb: 1, borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: alpha(theme.palette.common.white, 0.6), textTransform: 'uppercase', letterSpacing: 1 }}>
                      {section.title}
                    </Typography>
                  </Box>
                  {section.children.map((child, index) => {
                    const isChildActive = location.pathname === child.link;
                    return (
                      <MenuItem
                        key={index}
                        component={Link}
                        to={child.link}
                        onClick={handleCloseMenu}
                        sx={{
                          borderRadius: 1.5,
                          mb: 0.5,
                          gap: 1.5,
                          backgroundColor: isChildActive ? alpha(theme.palette.common.white, 0.15) : 'transparent',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.common.white, 0.08),
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 20, color: isChildActive ? 'common.white' : alpha(theme.palette.common.white, 0.6) }}>
                          {child.icon ? React.cloneElement(child.icon, { fontSize: "small" }) : null}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.title}
                          slotProps={{ primary: { fontSize: '0.85rem', fontWeight: isChildActive ? 700 : 400, color: isChildActive ? 'common.white' : alpha(theme.palette.common.white, 0.8) } }}
                        />
                      </MenuItem>
                    );
                  })}
                </Menu>
              )}

              {!isCollapsed && hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {section.children.map((child, index) => {
                      const isChildActive = location.pathname === child.link;

                      return (
                        <ListItemButton
                          key={index}
                          component={Link}
                          to={child.link}
                          sx={{
                            pl: 4,
                            borderRadius: 2,
                            mb: 0.5,
                            backgroundColor: isChildActive ? alpha(theme.palette.common.white, 0.2) : 'transparent',
                            color: isChildActive ? 'common.white' : alpha(theme.palette.common.white, 0.75),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.common.white, 0.08),
                            },
                            borderLeft: isChildActive ? `3px solid ${theme.palette.common.white}` : '3px solid transparent',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: isChildActive ? 'common.white' : alpha(theme.palette.common.white, 0.6) }}>
                            {child.icon ? React.cloneElement(child.icon, { fontSize: "small" }) : null}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.title}
                            slotProps={{ primary: { fontSize: '0.9rem', fontWeight: isChildActive ? 700 : 400, noWrap: true } }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
              {!isCollapsed && allowedMenuItems.indexOf(section) < allowedMenuItems.length - 1 && (
                <Divider sx={{ my: 1, borderColor: alpha(theme.palette.common.white, 0.12) }} />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};

export default CajonIzquierda;
